import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '@/lib/icons';
import { useAuth } from '@/lib/auth-context';
import { useLegalToggle } from '@/components/legal/LegalToggleContext';
import { useToast } from '@/components/ui/Toast';
import { useTheme } from '@/lib/theme';
import { useSound } from '@/lib/sound';
import { roleLabel } from '@/lib/format';
import { cn } from '@/lib/cn';
import { SkillSlot } from '@/skills/_core';

interface TopBarProps {
  mobileMenuOpen?: boolean;
  onToggleMobileMenu?: () => void;
}

export function TopBar({ mobileMenuOpen = false, onToggleMobileMenu }: TopBarProps = {}) {
  const { user, logout } = useAuth();
  const { showLegalHints, toggleLegalHints } = useLegalToggle();
  const { effective, toggle: toggleTheme } = useTheme();
  const {
    enabled: soundEnabled,
    setEnabled: setSoundEnabled,
    playClick,
    mutedByReducedMotion,
  } = useSound();
  const toast = useToast();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleToggleLegal = () => {
    toggleLegalHints();
    toast.info(
      'Rechtshinweise',
      showLegalHints ? 'Hinweise ausgeblendet.' : 'Hinweise eingeblendet.',
    );
  };

  return (
    <header className="sticky top-0 z-30 h-14 border-b border-ink-200 bg-paper/70 backdrop-blur-lg shadow-elev-2">
      <div className="flex h-full items-center gap-2 px-4 sm:gap-4 sm:px-6">
        {/* Hamburger (nur Mobile) */}
        {onToggleMobileMenu ? (
          <button
            type="button"
            onClick={onToggleMobileMenu}
            aria-label={mobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
            className="inline-flex h-11 w-11 items-center justify-center rounded text-ink-700 hover:bg-ink-100 md:hidden"
          >
            <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={20} />
          </button>
        ) : null}

        {/* Global Search — vom smart-search-Skill via Slot 'global.topbar' geliefert */}
        <div className="flex-1 max-w-md">
          <SkillSlot name="global.topbar" />
        </div>

        <div className="flex items-center gap-1">
          {/* Skill-Aktionen (Glocke, weitere Buttons) zwischen Suche und Legal-Toggle */}
          <SkillSlot name="global.topbar.actions" />

          {/* Legal toggle */}
          <button
            type="button"
            onClick={handleToggleLegal}
            aria-pressed={showLegalHints}
            title={
              showLegalHints
                ? 'Rechtshinweise ausblenden (Strg+J)'
                : 'Rechtshinweise einblenden (Strg+J)'
            }
            className={cn(
              'inline-flex h-11 w-11 items-center justify-center rounded transition-colors md:h-9 md:w-9',
              showLegalHints
                ? 'text-accent-700 hover:bg-accent-100 dark:text-accent-400'
                : 'text-ink-500 hover:bg-ink-100',
            )}
          >
            <Icon name="Scale" size={18} />
          </button>

          {/* Dark-Mode-Toggle */}
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={effective === 'dark' ? 'Light Mode' : 'Dark Mode'}
            title={effective === 'dark' ? 'Light Mode' : 'Dark Mode'}
            className="inline-flex h-11 w-11 items-center justify-center rounded text-ink-500 transition-colors hover:bg-ink-100 md:h-9 md:w-9"
          >
            <Icon name={effective === 'dark' ? 'Sun' : 'Moon'} size={18} />
          </button>

          {/* Sound-Toggle */}
          <button
            type="button"
            onClick={() => {
              const next = !soundEnabled;
              setSoundEnabled(next);
              if (next) playClick();
            }}
            aria-pressed={soundEnabled}
            title={
              mutedByReducedMotion
                ? 'Klick-Sound ist an, bleibt aber stumm: System steht auf „Bewegung reduzieren“'
                : soundEnabled
                  ? 'Klick-Sound ausschalten'
                  : 'Klick-Sound einschalten'
            }
            className={cn(
              'inline-flex h-11 w-11 items-center justify-center rounded transition-colors md:h-9 md:w-9',
              soundEnabled
                ? 'text-accent-700 hover:bg-accent-100 dark:text-accent-400'
                : 'text-ink-500 hover:bg-ink-100',
            )}
          >
            <Icon name={soundEnabled ? 'Volume2' : 'VolumeX'} size={18} />
          </button>

          {/* User-Menu */}
          <div ref={menuRef} className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className="inline-flex h-9 items-center gap-2 rounded border border-ink-200 bg-paper px-2.5 text-sm hover:border-ink-300"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ink-900 text-[11px] font-medium text-paper dark:bg-accent-500">
                {initials(user?.fullName)}
              </span>
              <span className="hidden text-ink-800 md:inline">{user?.fullName ?? '—'}</span>
              <Icon name="ChevronDown" size={14} className="text-ink-500" />
            </button>
            {menuOpen ? (
              <div className="absolute right-0 mt-2 w-60 rounded-lg border border-ink-200 bg-paper p-1 shadow-elev-3">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-ink-900">{user?.fullName}</p>
                  <p className="truncate text-xs text-ink-500">{user?.email}</p>
                  <span className="mt-1 inline-flex items-center rounded-full border border-ink-300 bg-ink-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ink-600">
                    {user ? roleLabel(user.role) : '—'}
                  </span>
                </div>
                <div className="my-1 h-px bg-ink-200" />
                <Link
                  to="/settings"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 rounded px-3 py-2 text-sm text-ink-700 hover:bg-ink-50"
                >
                  <Icon name="Settings2" size={14} />
                  Einstellungen
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-ink-700 hover:bg-ink-50"
                >
                  <Icon name="LogOut" size={14} />
                  Abmelden
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
}

function initials(name?: string | null): string {
  if (!name) return '–';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}
